import { useI18n } from '@ecom/i18n';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@ecom/ui-kit';
import { type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatCatalogPrice } from '../format-price';
import type { Category, ProductWithStock } from '../types';

import { FeaturedToggle } from './FeaturedToggle';
import { ProductStatusBadge } from './ProductStatusBadge';
import { StockStateBadge } from './StockStateBadge';

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-TN', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export function ProductTable({
  products,
  categoriesById,
}: {
  products: readonly ProductWithStock[];
  categoriesById: ReadonlyMap<string, Category>;
}): ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <Table caption={t('merchant.nav.products')}>
      <TableHead>
        <TableRow>
          <TableHeaderCell>{t('merchant.products.table.colProduct')}</TableHeaderCell>
          <TableHeaderCell align="end">{t('merchant.products.table.colPrice')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.orders.table.colStatus')}</TableHeaderCell>
          <TableHeaderCell align="center">
            {t('merchant.products.table.colFeatured')}
          </TableHeaderCell>
          <TableHeaderCell>{t('merchant.products.table.colStock')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.products.table.colCategory')}</TableHeaderCell>
          <TableHeaderCell>{t('merchant.products.table.colLastModified')}</TableHeaderCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {products.map((product) => (
          <TableRow key={product.id}>
            <TableCell>
              <button
                type="button"
                className="flex flex-col text-start"
                onClick={() => {
                  void navigate(`/products/${product.id}`);
                }}
              >
                <span className="font-medium text-ink hover:text-accent hover:underline">
                  {product.name}
                </span>
                <span className="font-mono text-xs text-inkFaint">{product.sku}</span>
              </button>
            </TableCell>
            <TableCell align="end">
              <span className="num text-sm text-ink">{formatCatalogPrice(product.price)}</span>
            </TableCell>
            <TableCell>
              <ProductStatusBadge status={product.status} />
            </TableCell>
            <TableCell align="center">
              <FeaturedToggle product={product} variant="icon" />
            </TableCell>
            <TableCell>
              <StockStateBadge stock={product.stock} />
            </TableCell>
            <TableCell>
              <span className="text-sm text-inkSoft">
                {product.categoryId !== null
                  ? (categoriesById.get(product.categoryId)?.name ?? '—')
                  : '—'}
              </span>
            </TableCell>
            <TableCell>
              <span className="text-sm text-inkFaint">{formatDate(product.updatedAt)}</span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
